import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, FileText, ShieldCheck, AlertCircle, RotateCcw, Download } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Public Proposal Signing Page — `/sign/:token`
 *
 * No authentication. The token gates access. Shows the project summary +
 * effective scope bullets, asks for the signer's name (and optionally a drawn
 * signature), then POSTs to /api/public/proposal/{token}/sign which flips the
 * deal status to "Won" and stamps scope_signed_at on the backend.
 */
export default function ProposalSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  // Form state
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null); // sign response
  // Customer picks a warranty tier from the price picker on the summary card.
  // Defaults to whatever the rep pre-locked (data.chosen_option_id) or empty.
  const [selectedOptionId, setSelectedOptionId] = useState("");
  // NDL Warranty Upgrade opt-in — visible for any WC tier (10/15/20/25) with
  // a non-zero upgrade price. Backend returns the tier-specific label
  // ("Upgrade to NDL Warranty" or "…w/ Hail Rider"). Legacy state name
  // (`addHailRider`) kept to avoid churn — semantics broadened to NDL upgrade.
  const [addHailRider, setAddHailRider] = useState(false);

  // Lazy-load Google Fonts the FIRST time the signing page mounts (and only
  // once — guarded by a stable id on the <link> tag so navigating back and
  // forth doesn't pile up duplicate stylesheets).
  useEffect(() => {
    if (document.getElementById("gf-signature-fonts")) return;
    const link = document.createElement("link");
    link.id = "gf-signature-fonts";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Allura&family=Caveat&family=Dancing+Script&family=Great+Vibes&family=Pacifico&display=swap";
    document.head.appendChild(link);
  }, []);

  // Typed-signature font picker — lets the customer pick a cursive style for
  // their typed name. The chosen font is recorded with the signed name so the
  // PDF receipt can render it the same way.
  const SIGNATURE_FONTS = [
    { id: "Dancing Script", label: "Dancing Script", css: "'Dancing Script', cursive" },
    { id: "Great Vibes",   label: "Great Vibes",   css: "'Great Vibes', cursive" },
    { id: "Caveat",        label: "Caveat",        css: "'Caveat', cursive" },
    { id: "Pacifico",      label: "Pacifico",      css: "'Pacifico', cursive" },
    { id: "Allura",        label: "Allura",        css: "'Allura', cursive" },
  ];
  const [signFont, setSignFont] = useState(SIGNATURE_FONTS[0].id);
  const signFontCss = (SIGNATURE_FONTS.find((f) => f.id === signFont) || SIGNATURE_FONTS[0]).css;

  // Signature canvas
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  /**
   * HiDPI / Retina-sharp canvas setup.
   *
   * Sizes the bitmap to (CSS pixels × devicePixelRatio) and scales the 2D
   * context by DPR so all draw calls remain in CSS-pixel units. Wired as a
   * callback ref so it fires the instant the <canvas> mounts (the canvas
   * only renders AFTER the deal payload loads — a regular useEffect on mount
   * would see canvasRef.current === null and bail). The ResizeObserver
   * additionally re-fits on column-width changes and orientation flips.
   */
  const _hidpiCleanup = useRef(null);
  const setCanvasEl = useCallback((c) => {
    // Teardown of any prior observers when React swaps the canvas.
    if (_hidpiCleanup.current) { _hidpiCleanup.current(); _hidpiCleanup.current = null; }
    canvasRef.current = c;
    if (!c) return;
    const apply = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = c.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const targetW = Math.round(rect.width * dpr);
      const targetH = Math.round(rect.height * dpr);
      if (c.width === targetW && c.height === targetH) return;
      c.width = targetW;
      c.height = targetH;
      const ctx = c.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.clearRect(0, 0, rect.width, rect.height);
      setHasInk(false);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(c);
    const onResize = () => apply();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    _hidpiCleanup.current = () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/public/proposal/${token}`)
      .then((r) => {
        setData(r.data);
        if (r.data.primary_contact_name) setSignerName(r.data.primary_contact_name);
        if (r.data.primary_contact_email) setSignerEmail(r.data.primary_contact_email);
        // Pre-select the rep's locked-in option (if any), otherwise nothing —
        // customer must actively pick a tier before the Sign button unlocks.
        if (r.data.chosen_option_id) setSelectedOptionId(r.data.chosen_option_id);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Unable to load proposal"));
  }, [token]);

  const beginStroke = (e) => {
    const c = canvasRef.current;
    if (!c) return;
    drawingRef.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    // The 2D context is DPR-scaled (see the setup effect), so we draw in
    // CSS-pixel units — no `c.width / rect.width` correction needed.
    const rect = c.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const ctx = c.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0A0A0A";
  };
  const strokeMove = (e) => {
    if (!drawingRef.current) return;
    const c = canvasRef.current;
    if (!c) return;
    e.preventDefault?.();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const rect = c.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const ctx = c.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const endStroke = () => {
    drawingRef.current = false;
  };
  const clearInk = () => {
    const c = canvasRef.current;
    if (!c) return;
    // The CTX is DPR-scaled, so clear in CSS-pixel units.
    const rect = c.getBoundingClientRect();
    c.getContext("2d").clearRect(0, 0, rect.width || c.width, rect.height || c.height);
    setHasInk(false);
  };

  const submit = async () => {
    if (!signerName.trim()) {
      setError("Please enter your full name to sign.");
      return;
    }
    if (!accepted) {
      setError("Please accept the proposal to continue.");
      return;
    }
    const options = data?.proposal_options || [];
    if (options.length > 0 && !selectedOptionId) {
      setError("Please select a warranty tier to proceed.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const signature_data_url = hasInk && canvasRef.current
        ? canvasRef.current.toDataURL("image/png")
        : null;
      const r = await axios.post(`${API_BASE}/api/public/proposal/${token}/sign`, {
        signer_name: signerName.trim(),
        signer_email: signerEmail.trim(),
        accepted: true,
        signature_data_url,
        signature_font: signFont,   // chosen cursive style (None if customer drew)
        chosen_option_id: selectedOptionId,
        add_ndl_upgrade: addHailRider,
        // Legacy field name kept for back-compat with older backends.
        add_hail_rider: addHailRider,
      });
      setSuccess(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not record signature. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center p-8 bg-white rounded-sm border border-zinc-200 max-w-md">
          <AlertCircle className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
          <h1 className="text-xl font-black text-zinc-900 mb-2">Proposal Unavailable</h1>
          <p className="text-sm text-zinc-600">{error}</p>
          <p className="text-xs text-zinc-400 mt-4">If you believe this is a mistake, contact SealTech Building Solutions directly.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-zinc-500">Loading proposal…</div>
      </div>
    );
  }

  const alreadySigned = data.signed?.is_signed || !!success;
  const justSigned = success && !success.already_signed;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Banner */}
      <div className="bg-[#062B67] text-white py-6 px-6 sm:px-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] opacity-80">SealTech Building Solutions</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight mt-1" data-testid="proposal-title">
              {data.project_title}
            </h1>
            <div className="text-[11px] opacity-80 mt-0.5">
              {[data.client_address, data.client_city, data.client_state, data.client_zip].filter(Boolean).join(", ")}
            </div>
          </div>
          <FileText className="w-12 h-12 opacity-30" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-6 sm:p-10 space-y-6">
        {/* Project summary card + warranty picker */}
        {(() => {
          const options = data.proposal_options || [];
          const selected = options.find((o) => o.id === selectedOptionId);
          // Prefer new `ndl_upgrade_price` field; fall back to the legacy
          // `hail_rider_price` field for back-compat with older backends.
          const ndlPrice = selected ? Number(selected.ndl_upgrade_price ?? selected.hail_rider_price ?? 0) : 0;
          const ndlLabel = selected?.ndl_upgrade_label || (selected?.warranty_years >= 20
            ? "Upgrade to NDL Warranty w/ Hail Rider"
            : "Upgrade to NDL Warranty");
          const upgradeCost = addHailRider ? ndlPrice : 0;
          const runningTotal = (selected?.price || 0) + upgradeCost;
          return (
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Roof System</div>
              <div className="font-mono mt-1">{data.proposed_roof_type || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Project Total</div>
              <div className="font-mono mt-1 font-bold" data-testid="project-total">
                ${runningTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Client</div>
              <div className="mt-1">{data.client_company || data.client_name || "—"}</div>
            </div>
          </div>

          {/* Warranty tier picker — one radio per non-zero option. Customer
              must pick one before the Accept button unlocks. */}
          {options.length > 0 && (
            <div className="mt-5 pt-5 border-t border-zinc-200" data-testid="proposal-option-picker">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-700 mb-3">
                Select Your Warranty Tier
              </div>
              <div className="space-y-2">
                {options.map((opt) => {
                  const checked = selectedOptionId === opt.id;
                  return (
                    <label
                      key={opt.id}
                      className={`flex items-center gap-3 px-4 py-3 border rounded-sm cursor-pointer transition-colors ${
                        checked ? "border-blue-700 bg-blue-50 shadow-sm" : "border-zinc-300 hover:bg-zinc-50"
                      }`}
                      data-testid={`proposal-option-${opt.id}`}
                    >
                      <input
                        type="radio"
                        name="warranty-tier"
                        checked={checked}
                        onChange={() => { setSelectedOptionId(opt.id); setAddHailRider(false); }}
                        className="h-4 w-4"
                      />
                      <div className="flex-1 flex items-baseline justify-between gap-2">
                        <span className="font-bold text-sm">{opt.label}</span>
                        <span className="font-mono font-black text-base text-zinc-900">
                          ${opt.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>
              {/* NDL Warranty Upgrade opt-in — visible for any tier with
                  a non-zero upgrade price. Label reflects whether Hail
                  Rider is bundled in (20/25-yr) or not (10/15-yr). */}
              {selected && ndlPrice > 0 && (
                <label
                  className={`mt-3 flex items-center gap-3 px-4 py-3 border-2 rounded-sm cursor-pointer transition-colors ${
                    addHailRider ? "border-amber-500 bg-amber-50" : "border-dashed border-amber-300 hover:bg-amber-50/40"
                  }`}
                  data-testid="proposal-ndl-upgrade"
                >
                  <input
                    type="checkbox"
                    checked={addHailRider}
                    onChange={(e) => setAddHailRider(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <div className="flex-1 flex items-baseline justify-between gap-2 text-sm">
                    <span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mr-1">Optional</span>
                      <span className="font-bold">{ndlLabel}</span>
                    </span>
                    <span className="font-mono font-black text-zinc-900">
                      +${ndlPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </label>
              )}
            </div>
          )}
        </div>
          );
        })()}

        {/* Scope card */}
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <h2 className="font-heading text-lg font-black tracking-tight mb-3">{data.scope?.title || "Project Scope"}</h2>

          {data.scope?.scope_1_title && data.scope.scope_1?.length > 0 && (
            <div className="mb-5">
              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-700 mb-2">{data.scope.scope_1_title}</h3>
              <ul className="space-y-1.5 text-xs leading-relaxed text-zinc-800" data-testid="proposal-scope-1">
                {data.scope.scope_1.map((b, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-blue-700 font-mono">{i + 1}.</span>
                    {/* Scope bullets come from our own server-side templates
                        (e.g. "<b>Flashing:</b> All penetrations…") and can
                        include limited inline HTML. Rendering as raw text
                        showed the tags to customers verbatim. Templates
                        are trusted — no user-generated markup lands here. */}
                    <span dangerouslySetInnerHTML={{ __html: b }} />
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.scope?.scope_2_title && data.scope.scope_2?.length > 0 && (
            <div className="mb-5">
              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-700 mb-2">{data.scope.scope_2_title}</h3>
              <ul className="space-y-1.5 text-xs leading-relaxed text-zinc-800" data-testid="proposal-scope-2">
                {data.scope.scope_2.map((b, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-blue-700 font-mono">{i + 1}.</span>
                    <span dangerouslySetInnerHTML={{ __html: b }} />
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.scope?.key_advantages?.length > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-700 mb-2">Key Advantages</h3>
              <ul className="space-y-1.5 text-xs leading-relaxed text-zinc-800" data-testid="proposal-advantages">
                {data.scope.key_advantages.map((b, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-emerald-700">✓</span>
                    <span dangerouslySetInnerHTML={{ __html: b }} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Sign-off card */}
        {alreadySigned ? (
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-sm p-6" data-testid="proposal-already-signed">
            {(() => {
              const tsIso = success?.signed_at || data.signed?.signed_at || "";
              const tsDisplay = tsIso ? new Date(tsIso).toLocaleString() : "just now";
              return (
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-8 h-8 text-emerald-700 flex-shrink-0" />
                  <div>
                    <h2 className="font-heading text-xl font-black tracking-tight text-emerald-900">Proposal Accepted</h2>
                    <p className="text-sm text-emerald-800 mt-1">
                      Signed by <b>{success?.signed_by_name || data.signed?.signed_by_name || "—"}</b> on {tsDisplay}.
                    </p>
                    {justSigned && success?.deposit_invoice_number && (
                      <p className="text-xs text-emerald-700 mt-3" data-testid="proposal-deposit-receipt">
                        Your deposit invoice (<b>{success.deposit_invoice_number}</b>) is queued and the SealTech team will send it shortly. No further action needed on your end.
                      </p>
                    )}
                    {justSigned && !success?.deposit_invoice_number && (
                      <p className="text-xs text-emerald-700 mt-3">
                        Thank you. Your SealTech project team will be in touch shortly to coordinate the deposit and schedule.
                      </p>
                    )}
                    <a
                      href={`${API_BASE}/api/public/proposal/${token}/pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="proposal-download-signed-pdf"
                      className="mt-4 inline-flex items-center gap-2 px-4 h-10 text-[11px] font-bold uppercase tracking-[0.15em] bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" /> Download Signed Copy (PDF)
                    </a>
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="bg-white border-2 border-zinc-950 rounded-sm p-6" data-testid="proposal-sign-card">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1 flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3" /> Electronic Signature
            </div>
            <h2 className="font-heading text-xl font-black tracking-tight mb-4">Accept &amp; Sign</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-600 block mb-1.5">Full Name</label>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  className="w-full px-2.5 py-2 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-700"
                  data-testid="proposal-signer-name"
                  required
                />
                {signerName && (
                  <div className="mt-2 px-3 py-3 border-2 border-zinc-300 rounded-sm bg-white">
                    <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1">Preview Signature</div>
                    <div
                      data-testid="proposal-typed-signature-preview"
                      style={{ fontFamily: signFontCss, fontSize: "2.1rem", lineHeight: 1.1, color: "#0b0b0b" }}
                    >
                      {signerName}
                    </div>
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {SIGNATURE_FONTS.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setSignFont(f.id)}
                      data-testid={`sign-font-${f.id.toLowerCase().replace(/\s+/g, "-")}`}
                      style={{ fontFamily: f.css }}
                      className={`px-2.5 py-1 rounded-sm border text-base transition-colors ${signFont === f.id ? "bg-blue-700 text-white border-blue-700" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"}`}
                      title={`Use the "${f.label}" font for the typed signature`}
                    >
                      {signerName || f.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-600 block mb-1.5">Email (optional)</label>
                <input
                  type="email"
                  value={signerEmail}
                  onChange={(e) => setSignerEmail(e.target.value)}
                  className="w-full px-2.5 py-2 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-700"
                  data-testid="proposal-signer-email"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-600 block mb-1.5 flex items-center justify-between">
                <span>Sign Here (optional)</span>
                {hasInk && (
                  <button onClick={clearInk} type="button" className="inline-flex items-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-900" data-testid="proposal-clear-signature">
                    <RotateCcw className="w-3 h-3" /> Clear
                  </button>
                )}
              </label>
              <canvas
                ref={setCanvasEl}
                style={{ height: 140 }}
                className="w-full border-2 border-dashed border-zinc-300 rounded-sm bg-zinc-50 cursor-crosshair touch-none"
                onMouseDown={beginStroke}
                onMouseMove={strokeMove}
                onMouseUp={endStroke}
                onMouseLeave={endStroke}
                onTouchStart={beginStroke}
                onTouchMove={strokeMove}
                onTouchEnd={endStroke}
                data-testid="proposal-signature-canvas"
              />
            </div>

            <label className="flex items-start gap-2 mb-5 cursor-pointer" data-testid="proposal-accept-checkbox">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-blue-700"
              />
              <span className="text-xs text-zinc-700 leading-relaxed">
                I accept the scope of work above as the agreed deliverable for this project, and I authorize SealTech Building Solutions to proceed.
                I understand my typed name above functions as my legally binding electronic signature.
              </span>
            </label>

            {error && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 mb-4 rounded-sm" data-testid="proposal-error">
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={submit}
              disabled={busy || !accepted || !signerName.trim()}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 h-12 text-[11px] font-bold uppercase tracking-[0.15em] bg-blue-700 hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-sm transition-colors"
              data-testid="proposal-sign-submit"
            >
              {busy ? "Signing…" : "Accept & Sign Proposal"}
            </button>
          </div>
        )}

        <div className="text-center text-[10px] text-zinc-400 pt-4 border-t border-zinc-200">
          SealTech Building Solutions  ·  720-715-9955  ·  scope@sealtechsolutions.co
        </div>
      </div>
    </div>
  );
}
